/*
 * zdn-product.js
 *
 * Two custom elements and one variant bridge. Nothing here reimplements
 * add-to-cart, variant resolution or section rendering -- those stay with
 * product-form.js and product-info.js. This file only covers what those two
 * do not: the gallery, the mobile bar, and repainting the visible price on
 * the tap instead of after the round trip.
 *
 * A third element used to live here: <zdn-scale>, a bar that drew the selected
 * variant to proportion. It parsed dimensions out of option labels, and on any
 * product whose labels were not dimensional -- which is most of a catalogue
 * being pruned to five to ten products -- it deleted itself on load. It was in
 * no preset and in no template, so it had never rendered for a single shopper,
 * and it cost 90 lines of JavaScript, a schema block and a component's worth of
 * CSS to keep saying so. Removed.
 *
 * Failure policy: a bad or missing data island degrades a feature and logs.
 * It never removes an element from the page. A previous build silently called
 * remove() inside a catch, and two components deleted themselves on every
 * product view without anything appearing in the console.
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function canSubscribe() {
    return typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';
  }

  function readJSON(root, selector) {
    var node = root.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('[zdn] could not parse ' + selector, error);
      return null;
    }
  }

  /* ------------------------------------------------------------------ */
  /* gallery                                                             */
  /* ------------------------------------------------------------------ */

  if (!customElements.get('zdn-gallery')) {
    customElements.define(
      'zdn-gallery',
      class ZdnGallery extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-zdn-track]');
          if (!this.track) return;

          this.slides = Array.from(this.querySelectorAll('[data-zdn-slide]'));
          this.counter = this.querySelector('[data-zdn-count-now]');
          this.bar = this.querySelector('[data-zdn-bar]');
          this.live = this.querySelector('[data-zdn-live]');
          this.jumpers = Array.from(this.querySelectorAll('[data-zdn-goto]'));
          this.navButtons = Array.from(this.querySelectorAll('[data-zdn-dir]'));
          this.zoomers = Array.from(this.querySelectorAll('[data-zdn-zoom]'));
          this.total = this.slides.length;
          this.index = 0;
          this.dragged = false;

          this.onScroll = this.throttle(this.syncFromScroll.bind(this));
          this.track.addEventListener('scroll', this.onScroll, { passive: true });

          /*
           * Drag guard. Every photographic slide is a real button so the stage
           * is keyboard operable, which means a swipe that ends over the image
           * also lands a click on it. A swipe is not a request to open the
           * lightbox, so any pointer that travelled more than 10px suppresses
           * the click that follows it.
           */
          this.track.addEventListener(
            'pointerdown',
            (event) => {
              this.dragged = false;
              this.dragFrom = event.clientX;
            },
            { passive: true }
          );
          this.track.addEventListener(
            'pointermove',
            (event) => {
              if (this.dragFrom == null) return;
              if (Math.abs(event.clientX - this.dragFrom) > 10) this.dragged = true;
            },
            { passive: true }
          );

          /* Arrows move the stage while focus is anywhere inside the track. */
          this.track.addEventListener('keydown', (event) => {
            if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
            event.preventDefault();
            this.goTo(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
          });

          this.jumpers.forEach((button) => {
            button.addEventListener('click', () => {
              this.goTo(parseInt(button.dataset.zdnGoto, 10));
            });
          });

          this.navButtons.forEach((button) => {
            button.addEventListener('click', () => {
              this.goTo(this.index + (button.dataset.zdnDir === 'next' ? 1 : -1));
            });
          });

          this.setupLightbox();

          this.paint();

          if (canSubscribe()) {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
              var mediaId = event && event.data && event.data.variant && event.data.variant.featured_media
                ? event.data.variant.featured_media.id
                : null;
              if (mediaId) this.showMedia(mediaId);
            });
          }
        }

        disconnectedCallback() {
          if (this.track) this.track.removeEventListener('scroll', this.onScroll);
          if (this.unsubscribe) this.unsubscribe();
        }

        throttle(fn) {
          var queued = false;
          return function () {
            if (queued) return;
            queued = true;
            window.requestAnimationFrame(function () {
              queued = false;
              fn();
            });
          };
        }

        syncFromScroll() {
          var width = this.track.clientWidth;
          if (!width) return;
          var next = Math.round(this.track.scrollLeft / width);
          if (next === this.index) return;
          this.index = next;
          this.paint();
        }

        /*
         * focusTrack is only passed by the keyboard path. A pointer user who
         * taps an arrow must not have focus yanked into the track; a keyboard
         * user pressing Left/Right already has it there.
         */
        goTo(index, focusTrack) {
          var last = this.slides.length - 1;
          var wanted = Math.max(0, Math.min(index, last));
          if (wanted === this.index && this.track.scrollLeft === wanted * this.track.clientWidth) return;
          this.index = wanted;
          this.track.scrollTo({
            left: this.index * this.track.clientWidth,
            behavior: REDUCED ? 'auto' : 'smooth',
          });
          this.paint();
          /*
           * By slide index, not by array position: a product whose second
           * media is a video has no zoom button at that position, so the two
           * lists are not parallel.
           */
          if (focusTrack) {
            var landing = this.zoomers.find((node) => parseInt(node.dataset.zdnZoom, 10) === this.index);
            if (landing) landing.focus({ preventScroll: true });
          }
        }

        showMedia(mediaId) {
          var target = this.slides.findIndex(function (slide) {
            return slide.dataset.zdnMediaId === String(mediaId);
          });
          if (target > -1 && target !== this.index) this.goTo(target);
        }

        paint() {
          var human = this.index + 1;
          if (this.counter) this.counter.textContent = String(human);
          if (this.live) {
            var of = this.live.dataset.zdnLiveOf || '/';
            this.live.textContent = human + ' ' + of + ' ' + this.total;
          }
          if (this.bar) this.bar.style.width = (human / this.total) * 100 + '%';

          this.jumpers.forEach((button) => {
            var isCurrent = parseInt(button.dataset.zdnGoto, 10) === this.index;
            button.setAttribute('aria-current', isCurrent ? 'true' : 'false');
            /*
             * Seventeen thumbnails do not fit one rail, so the active one is
             * pulled into view. `nearest` rather than `center` so a rail that
             * already shows the active thumbnail does not lurch sideways on
             * every swipe.
             */
            if (isCurrent && button.scrollIntoView) {
              button.scrollIntoView({
                behavior: REDUCED ? 'auto' : 'smooth',
                block: 'nearest',
                inline: 'nearest',
              });
            }
          });

          this.navButtons.forEach((button) => {
            var atStart = this.index === 0;
            var atEnd = this.index === this.slides.length - 1;
            button.disabled = button.dataset.zdnDir === 'next' ? atEnd : atStart;
          });
        }

        /* ---------------- lightbox ---------------- */

        setupLightbox() {
          this.dialog = this.querySelector('[data-zdn-lb]');
          this.lbStage = this.querySelector('[data-zdn-lb-stage]');
          this.lbNow = this.querySelector('[data-zdn-lb-now]');
          if (!this.dialog || !this.lbStage) return;

          /*
           * No showModal means no focus trap and no top layer, and an overlay
           * without either is worse than no overlay: the zoom buttons stay,
           * but they stop claiming to open anything.
           */
          if (typeof this.dialog.showModal !== 'function') {
            this.zoomers.forEach(function (button) {
              button.removeAttribute('data-zdn-zoom');
            });
            return;
          }

          this.zoomers.forEach((button) => {
            button.addEventListener('click', (event) => {
              if (this.dragged) {
                event.preventDefault();
                this.dragged = false;
                return;
              }
              this.openLightbox(parseInt(button.dataset.zdnZoom, 10));
            });
          });

          var closer = this.querySelector('[data-zdn-lb-close]');
          if (closer) closer.addEventListener('click', () => this.dialog.close());

          Array.from(this.querySelectorAll('[data-zdn-lb-dir]')).forEach((button) => {
            button.addEventListener('click', () => {
              this.moveLightbox(button.dataset.zdnLbDir === 'next' ? 1 : -1);
            });
          });

          this.dialog.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowRight') {
              event.preventDefault();
              this.moveLightbox(1);
            } else if (event.key === 'ArrowLeft') {
              event.preventDefault();
              this.moveLightbox(-1);
            }
          });

          /*
           * Clicking the backdrop closes. The backdrop is not a separate
           * element, so the test is whether the point is outside the dialog's
           * own box rather than what the target is.
           */
          this.dialog.addEventListener('click', (event) => {
            if (event.target !== this.dialog) return;
            var box = this.dialog.getBoundingClientRect();
            var outside =
              event.clientX < box.left ||
              event.clientX > box.right ||
              event.clientY < box.top ||
              event.clientY > box.bottom;
            if (outside) this.dialog.close();
          });

          /*
           * Closing returns the stage to whatever the lightbox was last
           * showing, so a shopper who arrowed from image 3 to image 9 inside
           * the overlay does not land back on image 3. The browser returns
           * focus to the button that opened it; moving the stage keeps what
           * that button now points at honest.
           */
          this.dialog.addEventListener('close', () => {
            if (this.lbIndex != null) this.goTo(this.lbIndex);
          });
        }

        openLightbox(index) {
          this.lbIndex = index;
          this.paintLightbox();
          this.dialog.showModal();
        }

        moveLightbox(step) {
          var zoomable = this.zoomers.length;
          if (!zoomable) return;
          var order = this.zoomers.map(function (button) {
            return parseInt(button.dataset.zdnZoom, 10);
          });
          var at = order.indexOf(this.lbIndex);
          if (at < 0) at = 0;
          var next = (at + step + zoomable) % zoomable;
          this.lbIndex = order[next];
          this.paintLightbox();
        }

        paintLightbox() {
          var button = this.zoomers.find((node) => parseInt(node.dataset.zdnZoom, 10) === this.lbIndex);
          if (!button) return;
          var img = this.lbStage.querySelector('img');
          if (!img) {
            img = document.createElement('img');
            img.className = 'zdn-gal__lbimg';
            img.decoding = 'async';
            this.lbStage.appendChild(img);
          }
          img.src = button.dataset.zdnFull;
          img.alt = button.dataset.zdnAlt || '';
          if (this.lbNow) this.lbNow.textContent = String(this.lbIndex + 1);
        }
      }
    );
  }

  /* ------------------------------------------------------------------ */
  /* info dialog                                                         */
  /* ------------------------------------------------------------------ */

  /*
   * A native <dialog>, so the focus trap, the inert background, Escape and the
   * top-layer stacking are the browser's problem rather than ours. Hand-rolled
   * modals are where accessible overlays normally go wrong, and this page
   * already fights a consent banner at z-index 2000000 -- top layer sits above
   * every z-index there is, so the dialog cannot lose that fight.
   *
   * If showModal is missing the trigger hides itself. Offering a button that
   * does nothing is worse than not offering it.
   */
  if (!customElements.get('zdn-guide')) {
    customElements.define(
      'zdn-guide',
      class ZdnGuide extends HTMLElement {
        connectedCallback() {
          this.dialog = this.querySelector('dialog');
          this.opener = this.querySelector('[data-zdn-guide-open]');
          if (!this.dialog || !this.opener) return;

          if (typeof this.dialog.showModal !== 'function') {
            this.opener.hidden = true;
            return;
          }

          this.opener.addEventListener('click', () => this.dialog.showModal());

          var closer = this.querySelector('[data-zdn-guide-close]');
          if (closer) closer.addEventListener('click', () => this.dialog.close());

          /*
           * Clicking the backdrop closes. The backdrop is not a separate
           * element, so the test is whether the point is outside the dialog's
           * own box rather than what the target is.
           */
          this.dialog.addEventListener('click', (event) => {
            if (event.target !== this.dialog) return;
            var box = this.dialog.getBoundingClientRect();
            var outside =
              event.clientX < box.left ||
              event.clientX > box.right ||
              event.clientY < box.top ||
              event.clientY > box.bottom;
            if (outside) this.dialog.close();
          });
        }
      }
    );
  }

  /* ------------------------------------------------------------------ */
  /* mobile sticky bar                                                   */
  /* ------------------------------------------------------------------ */

  if (!customElements.get('zdn-sticky')) {
    customElements.define(
      'zdn-sticky',
      class ZdnSticky extends HTMLElement {
        connectedCallback() {
          this.root = this.closest('product-info');
          this.priceOut = this.querySelector('[data-zdn-sticky-price]');
          this.labelOut = this.querySelector('[data-zdn-sticky-label]');
          this.button = this.querySelector('[data-zdn-sticky-atc]');

          var watched = document.getElementById(this.dataset.zdnWatch || '');
          if (watched && 'IntersectionObserver' in window) {
            /*
             * While the real button is on screen the bar is redundant, so it
             * gets out of the way. Without an observer it simply stays put,
             * which is the safe direction to fail in.
             */
            this.observer = new IntersectionObserver(
              (entries) => {
                this.toggleAttribute('data-zdn-hide', entries[0].isIntersecting);
              },
              { rootMargin: '0px 0px -20% 0px' }
            );
            this.observer.observe(watched);
          }

          if (canSubscribe()) {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => this.syncFromPage());
          }
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
          if (this.unsubscribe) this.unsubscribe();
        }

        /* The server-rendered price element is the single source of truth. */
        syncFromPage() {
          if (!this.root) return;
          var section = this.dataset.zdnSection;
          var priceNode = this.root.querySelector('#price-' + section + ' .zdn-price__now');
          if (priceNode && this.priceOut) this.priceOut.textContent = priceNode.textContent.trim();

          var main = document.getElementById('ProductSubmitButton-' + section);
          if (!main || !this.button) return;
          this.button.disabled = main.hasAttribute('disabled');
          var mainLabel = main.querySelector('span');
          if (mainLabel && this.labelOut) this.labelOut.textContent = mainLabel.textContent.trim();
        }

        preview(variant) {
          if (this.priceOut && variant.price) this.priceOut.textContent = variant.price;
          if (!this.button) return;
          this.button.disabled = !variant.available;
          if (this.labelOut && !variant.available && window.variantStrings) {
            this.labelOut.textContent = window.variantStrings.soldOut;
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------------ */
  /* opt-in recommendations, filtered                                    */
  /* ------------------------------------------------------------------ */

  /*
   * This element only exists when a merchant has explicitly switched the
   * automatic fallback on AND no curated list produced anything. It is the
   * only place on this page where a product nobody chose can appear, so it is
   * the only place that needs a guard.
   *
   * THE GUARD. This catalogue contains counterfeit "First Copy" replica
   * listings priced at one rupee, and Shopify's recommendation algorithm
   * surfaced them next to paid traffic -- which is why the stock
   * recommendations section was deleted from this page. Two filters run before
   * anything is drawn:
   *   1. a minimum price, compared in paise against the API's own integer
   *      cents so no locale-dependent string parsing sits between the number
   *      and the decision;
   *   2. a term matched case-insensitively against the title, the handle, the
   *      product type and the tags, because the same listing has been titled
   *      "First Copy", "first-copy" and "1st copy replica" at different times.
   * A candidate that fails either test is dropped and never reaches the DOM.
   * Verified against the four real replica listings on this shop: each one is
   * blocked independently by BOTH filters, so either alone is sufficient.
   *
   * Failure policy: any failure -- fetch error, bad payload, empty result,
   * everything filtered out -- leaves the element hidden and the page ends at
   * the FAQ exactly as it does without this feature. It never renders a
   * heading over nothing.
   */
  if (!customElements.get('zdn-recs')) {
    customElements.define(
      'zdn-recs',
      class ZdnRecs extends HTMLElement {
        connectedCallback() {
          this.list = this.querySelector('[data-zdn-recs-list]');
          var id = this.dataset.zdnRecsId;
          if (!this.list || !id || !window.fetch) return;

          this.limit = parseInt(this.dataset.zdnRecsLimit, 10) || 4;
          this.floor = parseFloat(this.dataset.zdnRecsFloor);
          if (!isFinite(this.floor)) this.floor = 100;
          this.term = (this.dataset.zdnRecsTerm || '').trim().toLowerCase();

          /*
           * Deferred until the band is near the viewport. It is the last thing
           * on the page and it competes with nothing above it for bandwidth.
           */
          if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver(
              (entries) => {
                if (!entries[0].isIntersecting) return;
                this.observer.disconnect();
                this.load(id);
              },
              { rootMargin: '0px 0px 400px 0px' }
            );
            this.observer.observe(this);
          } else {
            this.load(id);
          }
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
        }

        load(id) {
          var url =
            window.Shopify && window.Shopify.routes && window.Shopify.routes.root
              ? window.Shopify.routes.root
              : '/';
          url += 'recommendations/products.json?product_id=' + encodeURIComponent(id) + '&limit=10&intent=related';

          fetch(url)
            .then((response) => (response.ok ? response.json() : null))
            .then((payload) => {
              var products = payload && Array.isArray(payload.products) ? payload.products : [];
              var kept = products.filter((item) => this.allowed(item)).slice(0, this.limit);
              if (!kept.length) return;
              kept.forEach((item) => this.list.appendChild(this.card(item)));
              /* the desktop grid reads its column count from this, so a short
                 row is a short grid rather than a full one with dead columns.
                 Set before unhiding so the first painted frame is already
                 correct. */
              this.list.style.setProperty('--zdn-rel-n', String(kept.length));
              this.hidden = false;
            })
            .catch((error) => {
              console.warn('[zdn] recommendations unavailable', error);
            });
        }

        allowed(item) {
          if (!item || item.available === false) return false;

          /*
           * The endpoint is documented to exclude the source product, but a
           * cross-sell card that links back to the page it is printed on is a
           * dead end, and trusting a remote service not to do it costs more
           * than checking.
           */
          if (String(item.id) === String(this.dataset.zdnRecsId)) return false;

          /* the API reports money in integer cents; the floor is in units */
          var cents = typeof item.price_min === 'number' ? item.price_min : item.price;
          if (typeof cents !== 'number') return false;
          if (cents < this.floor * 100) return false;

          if (this.term) {
            var haystack = [item.title, item.handle, item.type]
              .concat(Array.isArray(item.tags) ? item.tags : [])
              .join(' ')
              .toLowerCase();
            if (haystack.indexOf(this.term) > -1) return false;
          }
          return true;
        }

        money(cents) {
          var code =
            window.Shopify && window.Shopify.currency && window.Shopify.currency.active
              ? window.Shopify.currency.active
              : 'INR';
          try {
            return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
              style: 'currency',
              currency: code,
              maximumFractionDigits: 0,
            }).format(cents / 100);
          } catch (error) {
            return String(Math.round(cents / 100));
          }
        }

        /*
         * Built as DOM nodes with textContent, never as an HTML string. A
         * product title is merchant data and this is the one path on the page
         * where it arrives through a network response.
         */
        card(item) {
          var li = document.createElement('li');
          li.className = 'zdn-rel__item';

          var link = document.createElement('a');
          link.className = 'zdn-rel__link';
          link.href = item.url;

          var frame = document.createElement('span');
          frame.className = 'zdn-rel__frame';
          if (item.featured_image) {
            var img = document.createElement('img');
            img.className = 'zdn-rel__img';
            img.src = item.featured_image;
            img.alt = item.title || '';
            img.loading = 'lazy';
            img.decoding = 'async';
            img.width = 400;
            img.height = 400;
            frame.appendChild(img);
          }
          link.appendChild(frame);

          var title = document.createElement('span');
          title.className = 'zdn-rel__ttl';
          title.textContent = item.title || '';
          link.appendChild(title);

          var prices = document.createElement('span');
          prices.className = 'zdn-rel__prices';
          var now = document.createElement('span');
          now.className = 'zdn-rel__price';
          now.textContent = this.money(typeof item.price_min === 'number' ? item.price_min : item.price);
          prices.appendChild(now);

          /* only when it is genuinely higher -- never a manufactured discount */
          var was = typeof item.compare_at_price_min === 'number' ? item.compare_at_price_min : item.compare_at_price;
          var real = typeof item.price_min === 'number' ? item.price_min : item.price;
          if (typeof was === 'number' && was > real) {
            var old = document.createElement('s');
            old.className = 'zdn-rel__was';
            old.textContent = this.money(was);
            prices.appendChild(old);
          }
          link.appendChild(prices);

          li.appendChild(link);
          return li;
        }
      }
    );
  }

  /* ------------------------------------------------------------------ */
  /* variant bridge                                                      */
  /* ------------------------------------------------------------------ */

  function bridge(root) {
    var section = root.dataset.section;
    var variants = readJSON(root, '[data-zdn-variants]');
    var errorNode = root.querySelector('[data-zdn-error]');

    if (canSubscribe()) {
      subscribe(PUB_SUB_EVENTS.cartError, function (event) {
        if (!errorNode || !event || !event.data) return;
        var text = event.data.errors || event.data.message;
        if (Array.isArray(text)) text = text.join(', ');
        if (!text) return;
        errorNode.textContent = text;
        errorNode.removeAttribute('hidden');
      });

      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        if (errorNode) errorNode.setAttribute('hidden', '');
      });
    }

    if (!Array.isArray(variants) || !variants.length) return;

    function selectedOptionNames() {
      return Array.from(root.querySelectorAll('variant-selects fieldset input:checked')).map(function (input) {
        return input.value;
      });
    }

    function matchVariant(names) {
      if (!names.length) return null;
      return (
        variants.find(function (variant) {
          var options = variant.options || [];
          if (options.length !== names.length) return false;
          return options.every(function (option, index) {
            return option === names[index];
          });
        }) || null
      );
    }

    /*
     * Optimistic repaint. product-info.js fetches the section and overwrites
     * this with authoritative markup a moment later; this only removes the
     * dead time between the tap and that response, which on a slow Indian
     * mobile connection is the difference between "instant" and "broken".
     */
    function preview(variant) {
      var priceRoot = root.querySelector('#price-' + section);
      if (priceRoot) {
        var now = priceRoot.querySelector('.zdn-price__now');
        var was = priceRoot.querySelector('.zdn-price__was');
        var save = priceRoot.querySelector('.zdn-price__save');
        if (now && variant.price) now.textContent = variant.price;
        if (was) {
          if (variant.compare_at) {
            was.textContent = variant.compare_at;
            was.hidden = false;
          } else {
            was.hidden = true;
          }
        }
        if (save) {
          if (variant.save) {
            save.textContent = variant.save;
            save.hidden = false;
          } else {
            save.hidden = true;
          }
        }
      }

      var main = document.getElementById('ProductSubmitButton-' + section);
      if (main) {
        main.disabled = !variant.available;
        var label = main.querySelector('span');
        if (label && window.variantStrings) {
          label.textContent = variant.available
            ? window.variantStrings.addToCart
            : window.variantStrings.soldOut;
        }
      }

      var sticky = root.querySelector('zdn-sticky');
      if (sticky && typeof sticky.preview === 'function') sticky.preview(variant);
    }

    if (canSubscribe()) {
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, function (event) {
        if (!event || !event.data || !root.contains(event.data.event.target)) return;
        var match = matchVariant(selectedOptionNames());
        if (match) preview(match);
      });
    }
  }

  function init() {
    document.querySelectorAll('product-info.zdn-product').forEach(bridge);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
